/**
 * WorkerLeave — worker portal leave page.
 *
 * Shows each LeaveType's remaining balance, plus a "Request leave" CTA that
 * routes to the SINGLE unified attendance-request form
 * (/me/attendance-requests?compose=leave). The old in-page rich leave dialog
 * was removed (owner 2026-06-28) so there is ONE leave-request form across the
 * portal — matching the mobile app, which already routes its Leave page to the
 * unified attendance-requests screen. Half-day support now lives in that one
 * form, so nothing is lost by consolidating.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, CircularProgress, LinearProgress, Paper, Stack, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { meApi } from '../../api';

interface LeaveType {
  id: string;
  name: string;
  status?: string;
  allow_negative_balance?: boolean;
}

interface LeaveBalance {
  id: string;
  leave_type_id: string;
  entitlement: number | string;
  used: number | string;
  carried_over: number | string;
  // Either the balance row's own fields or the dashboard-derived shape.
  leave_type_name?: string;
  remaining?: number;
  total?: number;
}

const WorkerLeave: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [types, setTypes] = useState<LeaveType[]>([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [balData, typeData] = await Promise.all([
        meApi.leaveBalances.list() as Promise<unknown>,
        meApi.leaveTypes.list() as Promise<unknown>,
      ]);
      setBalances(Array.isArray(balData) ? (balData as LeaveBalance[]) : []);
      setTypes(Array.isArray(typeData) ? (typeData as LeaveType[]) : []);
    } catch {
      // Best-effort: a balance load failure still shows the empty state + CTA.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const typeNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const lt of types) m.set(lt.id, lt.name);
    return m;
  }, [types]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {t('worker:leave.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('worker:leave.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => navigate('/me/attendance-requests?compose=leave')}
          disabled={types.length === 0}
          sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
        >
          {t('worker:leave.requestLeave')}
        </Button>
      </Box>

      {balances.length === 0 ? (
        <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">{t('worker:leave.noBalance')}</Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {t('worker:leave.noBalanceHint')}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 2, width: '100%' }}>
          {balances.map((b) => {
            const used = Number(b.used ?? 0);
            const total = Number(b.entitlement ?? b.total ?? 0);
            const carried = Number(b.carried_over ?? 0);
            const cap = total + carried;
            const remaining = b.remaining != null ? Number(b.remaining) : Math.max(cap - used, 0);
            const pct = cap > 0 ? (used / cap) * 100 : 0;
            const exhausted = remaining <= 0;
            const lowBalance = remaining > 0 && remaining < 2;
            const name = b.leave_type_name ?? typeNameById.get(b.leave_type_id) ?? t('worker:attendanceRequests.types.leave');
            return (
              <Paper
                key={b.id ?? b.leave_type_id}
                elevation={0}
                sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>{name}</Typography>
                <Stack direction="row" spacing={1.5} alignItems="flex-end" sx={{ mb: 1.25 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: exhausted ? 'error.main' : lowBalance ? 'warning.main' : 'primary.main', lineHeight: 1 }}
                  >
                    {remaining}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ pb: 0.5 }}>
                    {t('worker:leave.daysRemaining', { total })}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(pct, 100)}
                  sx={{ height: 4, borderRadius: 2, bgcolor: 'grey.100', '& .MuiLinearProgress-bar': { bgcolor: pct > 80 ? 'warning.main' : 'primary.main' } }}
                />
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">{t('worker:leave.used', { count: used })}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('worker:leave.total', { count: total })}</Typography>
                </Stack>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default WorkerLeave;
